// apps/web/src/pages/author/constraint-config/propagation-config.tsx
//
// Phase 9 Plan 07 Task 1 — Propagation constraint authoring sub-form
// (CSTR-01 partial + CSTR-05 explanation surface).
//
// Schema source-of-truth (packages/shared/src/schemas/constraint.ts lines 39-48):
//   { kind: "propagation";
//     trigger: StateRef { widgetInstanceId: string min 1, pathInState: string min 1 };
//     effect: "mirror-all-batch-sets" | "lock-to-batch-set-zero" | "inherit-from-batch-set-zero";
//     explanation: 1-500 chars }
//
// UI-SPEC §"Propagation" copy table (lines 477-483):
//   Helper: "When this option is selected, automatically select it in other
//   {subdivision.lower}s too."
//   Source label: "When this option is selected in" (target widget reference)
//
// authr-06 hygiene: per-path useWatch ONLY (handled by FormField). No top-level.

import { useFormContext } from "react-hook-form";
import { Fieldset } from "@/components/ui/fieldset";
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import type { ConstraintConfigSubFormProps } from "./types.js";

// =============================================================================
// PropagationConfigShape — local typing for the default factory.
// =============================================================================

export interface PropagationConfigShape {
  kind: "propagation";
  trigger: {
    widgetInstanceId: string;
    pathInState: string;
  };
  effect: "mirror-all-batch-sets" | "lock-to-batch-set-zero" | "inherit-from-batch-set-zero";
  explanation: string;
}

// =============================================================================
// Default factory
// =============================================================================

export function defaultPropagationConfig(): PropagationConfigShape {
  return {
    kind: "propagation",
    trigger: { widgetInstanceId: "", pathInState: "" },
    effect: "mirror-all-batch-sets",
    explanation: "",
  };
}

// =============================================================================
// PropagationConfigSubForm
// =============================================================================

export function PropagationConfigSubForm({
  constraintId: _constraintId,
  basePath,
}: ConstraintConfigSubFormProps): React.ReactElement {
  const { control } = useFormContext();

  return (
    <div className="flex flex-col gap-4">
      <Fieldset legend="PROPAGATION">
        <p className="text-xs text-muted-foreground">
          When this option is selected, automatically select it in other batch sets too.
        </p>

        <FormField
          control={control}
          name={`${basePath}.trigger.widgetInstanceId`}
          render={({ field: f }) => (
            <FormItem data-field-path={`${basePath}.trigger.widgetInstanceId`}>
              <FormLabel>Trigger widget</FormLabel>
              <FormControl>
                <Input
                  {...f}
                  value={(f.value as string | undefined) ?? ""}
                  placeholder="widget-instance-id"
                  data-testid="propagation-trigger-widget"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name={`${basePath}.trigger.pathInState`}
          render={({ field: f }) => (
            <FormItem data-field-path={`${basePath}.trigger.pathInState`}>
              <FormLabel>Trigger state path</FormLabel>
              <FormControl>
                <Input
                  {...f}
                  value={(f.value as string | undefined) ?? ""}
                  placeholder="selectedFlavors"
                  data-testid="propagation-trigger-path"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name={`${basePath}.effect`}
          render={({ field: f }) => (
            <FormItem data-field-path={`${basePath}.effect`}>
              <FormLabel>Effect</FormLabel>
              <Select
                value={(f.value as string | undefined) ?? "mirror-all-batch-sets"}
                onValueChange={f.onChange}
              >
                <FormControl>
                  <SelectTrigger data-testid="propagation-effect">
                    <SelectValue placeholder="Pick an effect" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="mirror-all-batch-sets">Mirror to all batch sets</SelectItem>
                  <SelectItem value="lock-to-batch-set-zero">Lock to batch set zero</SelectItem>
                  <SelectItem value="inherit-from-batch-set-zero">
                    Inherit from batch set zero
                  </SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
      </Fieldset>

      <Fieldset legend="EXPLANATION">
        <FormField
          control={control}
          name={`${basePath}.explanation`}
          render={({ field: f }) => (
            <FormItem data-field-path={`${basePath}.explanation`}>
              <FormLabel>Explanation</FormLabel>
              <FormControl>
                <Textarea
                  {...f}
                  value={(f.value as string | undefined) ?? ""}
                  rows={3}
                  maxLength={500}
                  placeholder="Shown to authors and viewers when this rule disables an option. Required."
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </Fieldset>
    </div>
  );
}
PropagationConfigSubForm.displayName = "PropagationConfigSubForm";
